/**
 * PromptModal.js — reusable pitch prompt, clear of the viewport controls.
 *
 * The modal is intentionally small: it must not block the
 * viewport behind it, because prompts like "Attach to Player" expect the
 * user to click something on the pitch as the primary (main) action.
 *
 * prompt = {
 *   title:        string            — bold header line
 *   description:  string            — what the user is expected to do
 *   mainAction:   fn(...args)       — invoked by the CALLER when the primary
 *                                    interaction happens (e.g. the user clicks
 *                                    a player on the pitch). Not rendered as a
 *                                    button; the caller owns when it fires.
 *   subActions:   [{ label, action, icon?, utility?, danger? }] — secondary buttons
 *                                    (optional SVG icon shown before the label).
 *                                    A UTILITY option (utility: true) is a verb
 *                                    that belongs to the same object but is not
 *                                    an answer to the question: it shows at the
 *                                    top without a number, and takes neither a
 *                                    number key nor the Enter focus.
 *                                    A DANGEROUS option (danger: true) is tinted
 *                                    like the Cancel button, so "delete this"
 *                                    never wears the same colour as "Kick".
 *   cancelAction: fn                — dismiss + cancel any effect of mainAction
 * }
 *
 * Keyboard: while a prompt is open, App maps number keys 1–9 to the NUMBERED
 * subActions (data.promptNumberedOptions — the same list that prints the
 * badges) and 0 to cancelAction; the buttons show those numbers as hints.
 *
 * Layout:
 *   | Prompt Title | red X |
 *   Description of action expected
 *   | subAction 1 | subAction 2 | … |
 */

import { useEffect, useRef } from 'react';
import { promptNumberedOptions } from './data';
import { useI18n } from './i18n';
import { ContextCloseBar, contextPanelPosition } from './ContextPanel';
import MenuActionIcon from './MenuActionIcon';
import ActionTooltipButton from './ActionTooltipButton';

const btnBase = {
  fontSize: '11px',
  fontWeight: '700',
  padding: '7px 10px',
  // A thumb, not a cursor. This is the most-used panel in the build — every
  // verb on the pitch, the ruler and the branch tree is answered here — and a
  // one-line entry used to measure ~30px tall, below the size the rest of the
  // touch UI settled on. The long labels already wrap to two lines and clear
  // it; this is the floor for the short ones ("⤡ Quicker", "↩ Undo").
  minHeight: 32,
  borderRadius: '6px',
  border: '1px solid transparent',
  cursor: 'pointer',
  // WRAP, do not spill. The panel is capped at 180–230px so it never covers the
  // pitch; a label longer than that used to run straight out of the box (a
  // double-click on a player printed text over the touchline). Two short lines
  // inside the panel beat one long line outside it.
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  textAlign: 'left',
  lineHeight: '1.3',
};

export default function PromptModal({ prompt, bottom = 0 }) {
  // Keyboard flow: focus the FIRST answer button when a prompt opens (or its
  // text changes) — Enter then accepts that first option, exactly like the
  // number key 1. App's Enter/Space handlers stand aside while focus is
  // inside the panel, so native button activation wins.
  const firstBtnRef = useRef(null);
  // The Cancel label is the one word this panel owns itself — everything else
  // arrives already spoken by the caller. It sat here as an English literal for
  // the whole life of the app, on the most-used panel in the build.
  const { t } = useI18n();
  useEffect(() => {
    if (prompt && firstBtnRef.current) firstBtnRef.current.focus();
  }, [prompt]);
  if (!prompt) return null;
  // Which options the number keys answer, and in what order — the SAME list the
  // key handler uses (data.promptNumberedOptions), so the number printed on a
  // button is always the key that fires it. Utility options (⇄ Mirror) sit at
  // the top for the eye without stealing 1–9 or the Enter focus.
  const numberedOptions = promptNumberedOptions(prompt);
  const firstNumbered = numberedOptions[0];
  const placement = contextPanelPosition(bottom);
  const actions = prompt.subActions || [];
  const entries = prompt.iconOnly
    ? ['actions', 'time', 'visual'].flatMap(section => {
      const items = actions.filter(sa => (sa.section || (sa.icon ? 'actions' : 'visual')) === section);
      return items.length ? [{ heading: section }, ...items.map(sa => ({ sa }))] : [];
    })
    : actions.map(sa => ({ sa }));
  return (
    <div
      className={`kv-context-panel kv-prompt-panel${prompt.iconOnly ? ' kv-action-palette' : ''}`}
      role={prompt.iconOnly ? 'group' : undefined}
      aria-label={prompt.iconOnly ? prompt.title : undefined}
      style={{
        ...placement,
        zIndex: 250,
        background: 'rgba(15, 23, 42, 0.97)',
        border: '1px solid #a78bfa',
        borderRadius: '10px',
        boxShadow: '0 12px 34px rgba(0,0,0,0.65)',
        padding: 0,
        minWidth: 0, width: prompt.iconOnly ? '146px' : 'max-content', boxSizing: 'border-box',
        maxWidth: prompt.iconOnly ? 'calc(100vw - 16px)' : 'min(178px, calc(100vw - 16px))',
        height: prompt.iconOnly ? placement.maxHeight : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        textAlign: 'center',
      }}
    >
      <ContextCloseBar label={t('fb.close')} onClick={prompt.cancelAction} />
      {!prompt.iconOnly && <div title={prompt.title} style={{ margin: '10px 12px 0', fontSize: '12px', fontWeight: '800', color: '#f8fafc', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prompt.title}</div>}
      {!prompt.iconOnly && prompt.description && <div title={prompt.description} style={{ margin: '6px 12px 0', fontSize: '10px', color: '#cbd5e1', lineHeight: '1.3', textAlign: 'left', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{prompt.description}</div>}
      <div style={prompt.iconOnly ? { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', alignContent: 'safe center', gap: 4, padding: 4, flex: 1 } : { display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px 12px 10px' }}>
        {entries.map((entry, i) => {
          if (entry.heading) return <div key={entry.heading} className="kv-palette-heading" style={{ gridColumn: '1 / -1', textAlign: 'left', color: '#94a3b8', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '5px 3px 1px' }}>{t(`act.section.${entry.heading}`)}</div>;
          const sa = entry.sa;
          const n = numberedOptions.indexOf(sa) + 1;
          return (
            <ActionTooltipButton
              key={i}
              ref={sa === firstNumbered ? firstBtnRef : undefined}
              label={sa.label}
              action={sa.action}
              tooltipEnabled={!!prompt.iconOnly}
              aria-label={`${n > 0 ? `${n} ` : ''}${sa.label}`}
              style={{
                ...btnBase,
                // A destructive verb must not wear the same colour as an answer.
                // Before this, "✕ Delete this key" sat next to "Kick" in an
                // identical slate button, one thumb-width apart.
                background: sa.danger ? '#7f1d1d' : (sa.utility ? '#1e293b' : '#334155'),
                color: sa.danger ? '#fecaca' : '#e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: prompt.iconOnly ? 'center' : undefined, gap: '5px',
                ...(prompt.iconOnly ? { width: 42, height: 42, minHeight: 42, padding: 0 } : {}),
              }}
            >
              {prompt.iconOnly ? (sa.icon || <MenuActionIcon name={sa.iconKey || (sa.danger ? 'delete' : 'action')} />) : <>
                {n > 0 && n < 10 && <span style={{ display: 'grid', placeItems: 'center', flex: '0 0 20px', width: 20, height: 20, borderRadius: 5, background: 'rgba(255,255,255,0.12)', fontSize: '11px', fontWeight: '800' }}>{n}</span>}
                {sa.icon}
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sa.label}</span>
              </>}
            </ActionTooltipButton>
          );
        })}
      </div>
    </div>
  );
}
